/**
 * RepertoirePlayContainerOSMD
 *
 * React component that hosts the PhaserGame for OSMD-based repertoire rendering.
 * Parallels RepertoirePlayContainer (SVG version) but replaces the SVG Staff component
 * with a Phaser scene (OSMDRepertoireScene) that renders MusicXML directly.
 *
 * Responsibilities:
 * - Mount PhaserGame and start OSMDRepertoireScene
 * - Send phrase data to the scene via AddOSMDPhrase events
 * - Forward MIDI events to Phaser for NoteContainer visual feedback
 * - Forward play/pause state changes to Phaser
 * - Listen for RepertoireCompleted / RepertoirePhraseCompleted from Phaser
 * - Dispatch backend tracking events (play, pause, playing, completed, phrase completed)
 * - Forward soft-refresh to Phaser when updateKey changes
 */

import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import Phaser from 'phaser';
import { useDispatch, useSelector } from 'react-redux';
import { usePageVisibility } from 'react-page-visibility';
import { useLocation } from 'react-router-dom';
import ReactGA from 'react-ga4';
import { find } from 'lodash';
import { NoteType } from '@museflow-ai/opensheetmusicdisplay';

import { PhaserGame } from 'Phaser/PhaserGame';
import type { IRefPhaserGame } from 'Phaser/PhaserGame';
import { OSMDEventStream } from 'Phaser/GameObjects/OSMDEventStream';
import { TimingOffsetsConfig } from 'Models/EventStream';
import Phrase from 'Models/Phrase';
import { useRepertoirePlayContext } from 'Contexts/RepertoirePlayContext';
import {
  NotifyPhaser,
  AddOSMDPhrase,
  StartExercise,
  PauseExercise,
  ChangeTempo,
  SoftRefreshRepertoire as SoftRefreshRepertoireEvent,
  ListenForPhaserEvent,
  ListenPhaserReady,
  ListenStartedPlaying,
  ListenRepertoireCompleted,
  ListenRepertoirePhraseCompleted,
  ListenExercisePaused,
  StartPlayingArgs,
  ExerciseToReactEventType,
  RemoveListener,
  ResizeWindow,
} from 'Phaser/EventBus';
import { RepertoireReducer } from 'Types/RepertoireTypes';
import { MainAppReducer, ErrorRecognitionActive } from 'Types';
import * as eventActions from 'Actions/events';
import { CustomCircularProgress } from 'Components/StyledComponents';
import { IntervalTimer } from 'Utils/IntervalTimer';
import logger from 'Utils/Logger';
import { awsRum } from 'Utils/AwsRum';
import { debounce } from 'lodash';
import { getNamesConcatted } from 'Utils';
import type { AppDispatch } from 'Reducers';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface RepertoirePlayContainerOSMDProps {
  showKeyboard: boolean;
  setAccuracy: (accuracy: number) => void;
  setShowAccuracy: (show: boolean) => void;
  cursorIsVisible: boolean;
  scrollPosition?: number | null;
  setScrollPosition?: React.Dispatch<React.SetStateAction<number | null>>;
}

// How often (ms) to dispatch the repertoirePlayingEventAction
const PLAY_TIME_SEND_INTERVAL_MS = 10_000;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const RepertoirePlayContainerOSMD: React.FC<RepertoirePlayContainerOSMDProps> = (props) => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const isPageVisible = usePageVisibility();

  // ---- Analytics ----
  useEffect(() => {
    awsRum?.recordPageView(location.pathname);
    ReactGA?.send({ hitType: 'pageview', page: location.pathname });
  }, [location.pathname]);

  // ---- Context ----
  const {
    midiStream,
    phraseParts,
    allPhrasesLoaded,
    musicLoaded,
    setMusicLoaded,
    performanceTempo,
    updateKey,
    setDeferSchedulerStart,
    startScheduler,
    setForwardMidiToPhaser,
    scheduler2,
  } = useRepertoirePlayContext();

  // ---- Redux ----
  const appData = useSelector((state: MainAppReducer) => state.mainAppReducer);
  const errorRecognitionActive: ErrorRecognitionActive = appData.errorRecognitionActive;
  const repertoireReducerData = useSelector(
    (state: RepertoireReducer) => state.repertoireReducer
  );
  const repertoireIdSelect = repertoireReducerData.repertoireIdSelect;
  const repertoirePlaying = repertoireReducerData.repertoirePlaying;
  const userRepertoireProgress = repertoireReducerData.userRepertoireProgresses;
  const repertoireData = repertoireReducerData.repertoireData;

  // Derive tempo the same way RepertoirePlayContext does
  const repertoireSelected = useMemo(
    () => find(repertoireData, (rd) => rd.id === repertoireIdSelect),
    [repertoireData, repertoireIdSelect]
  );
  const repertoireProgressSelected = useMemo(
    () =>
      find(
        userRepertoireProgress,
        (urd) => urd?.repertoire?.id === repertoireIdSelect
      ),
    [userRepertoireProgress, repertoireIdSelect]
  );
  const currentTempo =
    repertoireProgressSelected?.last_tempo || performanceTempo || 72;

  // Derive play session ID from Redux
  const playSessionId = repertoireReducerData.playSessionId;

  // MIDI forwarding is now handled by the RepertoirePlayContext via
  // forwardMidiToPhaser flag — no separate MidiContext listener needed.

  // ---- Refs ----
  const phaserRef = useRef<IRefPhaserGame | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const phrasesSentRef = useRef(false);
  /** Latest repertoireSelected from Redux, stored in a ref so loadScene can
   *  read it without adding it to useCallback deps (which would destabilize
   *  the callback and risk missing the one-time PhaserReady event). */
  const repertoireSelectedRef = useRef(repertoireSelected);
  repertoireSelectedRef.current = repertoireSelected;
  const sceneReadyRef = useRef(false);
  const prevUpdateKeyRef = useRef(updateKey);
  const sendPlayTimeIntervalRef = useRef<IntervalTimer | undefined>(undefined);
  const accumulatedTimeRef = useRef(0);
  const lastMeasuredTimeRef = useRef(0);
  const isFirstPlayPauseRef = useRef(true);
  /** Whether the first MIDI note has been played (count-in completed).
   *  Used to distinguish first-play (deferred scheduler) from resume
   *  (scheduler must be recalibrated before the scene starts updating). */
  const hasPlayedOnceRef = useRef(false);
  /** The global measure the cursor was repositioned to on pause.
   *  Used on resume to pass as measureOffset to startScheduler() so the
   *  TimeKeeper starts from exactly where the cursor was placed. */
  const pausedAtMeasureRef = useRef<number | undefined>(undefined);
  /** The actual OSMD global timestamp where the cursor was positioned on pause.
   *  Used to compute the correct TimeKeeper startMeasure on resume, since
   *  the raw measure index * measureDurationAt(0) may not match the actual
   *  OSMD timestamp (especially in non-4/4 time signatures). */
  const pausedAtTimestampRef = useRef<number | undefined>(undefined);
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);
  const eventStreamUpdateLoopStartedRef = useRef(false);
  const accuracyLoopStartedRef = useRef(false);

  // ---- Defer scheduler start for OSMD ----
  // Tell the shared RepertoirePlayContext to skip scheduler2.unpauseTicks()
  // in onStart(). The scheduler will instead be started by startScheduler()
  // when the Phaser scene emits StartedPlaying (on the first MIDI note).
  useEffect(() => {
    setDeferSchedulerStart(true);
    return () => {
      setDeferSchedulerStart(false);
    };
  }, [setDeferSchedulerStart]);

  // ---- Forward MIDI to Phaser ----
  // Tell the shared RepertoirePlayContext to forward MIDI events to the
  // Phaser EventBus via NotifyPhaser(MidiOn/MidiOff). This lives in the
  // context's own MIDI listener (which survives clearListeners()) rather
  // than a separate listener that gets wiped by clearListeners().
  useEffect(() => {
    logger.debug('[OSMD-MIDI] Container: setting forwardMidiToPhaser = true');
    setForwardMidiToPhaser(true);
    return () => {
      logger.debug('[OSMD-MIDI] Container: setting forwardMidiToPhaser = false (unmount)');
      setForwardMidiToPhaser(false);
    };
  }, [setForwardMidiToPhaser]);

  // ---- Dimensions ----
  // Stable game dimensions passed to PhaserGame — set once and never updated.
  // PhaserGame's useLayoutEffect depends on [ref, height, width]; if these
  // change, the entire Phaser game is destroyed and recreated, wiping all
  // phrases, cursors, and event listeners. All subsequent resizing is handled
  // by the scene's resize() method via the ResizeWindow event bus event.
  const [gameWidth] = useState(() => Math.max(window.innerWidth, 960));
  // The header and footer are position:fixed overlays, so the canvas can fill
  // the full viewport height. Content behind the header/footer is handled by
  // the scene's top padding (for the toolbar) and is scrollable.
  const [gameHeight] = useState(() => Math.max(400, window.innerHeight));

  // Responsive wrapper height for the container div CSS — tracks available
  // vertical space so the Phaser canvas wrapper grows/shrinks with the window.
  // This state does NOT flow into PhaserGame props, so updating it won't
  // trigger game destruction.
  const [wrapperHeight, setWrapperHeight] = useState(
    () => Math.max(400, window.innerHeight)
  );

  // ---- OSMDEventStream for Phaser scene ----
  // Uses the SAME timeKeeper as the context's midiStream / scheduler2.
  // midiStream.timeKeeper is the context's single TimeKeeper instance,
  // always defined at mount time (unlike scheduler2 which starts null).
  const [osmdEventStream] = useState(
    () => {
      const stream = new OSMDEventStream(
        midiStream.timeKeeper,
        new TimingOffsetsConfig(),
        appData.userData?.colorblind_type || null,
        1,
        0.025,
        0.1,
        true
      );
      logger.debug('[OSMD-MIDI] Container: created osmdEventStream', {
        osmdEventStreamId: stream.id,
        contextMidiStreamId: midiStream.id,
        sharedTimeKeeper: '(same object ref from midiStream.timeKeeper)',
      });
      return stream;
    }
  );

  // ---- Phrases ----
  const phrases = useMemo((): Phrase[] => {
    if (allPhrasesLoaded) {
      const partsArray: Array<Phrase | undefined> = phraseParts.toArray();
      return partsArray.filter((phrase): phrase is Phrase => phrase !== undefined);
    }
    return [];
  }, [phraseParts, allPhrasesLoaded]);

  // Mark music as loaded when all phrases are ready
  useEffect(() => {
    if (allPhrasesLoaded) {
      setMusicLoaded(true);
    }
  }, [allPhrasesLoaded, setMusicLoaded]);

  // BPM sync is handled by the context (RepertoirePlayContext line 865:
  // timeKeeper.setBpm(tempo)). Since osmdEventStream shares the same
  // timeKeeper via midiStream.timeKeeper, no additional sync is needed here.

  // ---- Sync error recognition (hand separation) to osmdEventStream ----
  // The context syncs errorRecognitionActive to midiStream (line 324), but in
  // OSMD mode midiStream has no events — osmdEventStream is the one that
  // processes MIDI input, colors notes, and calculates accuracy. Without this
  // sync, the hand toggle in the UI has no effect on note matching or coloring.
  useEffect(() => {
    osmdEventStream.setErrorRecognitionActive(errorRecognitionActive);
    logger.debug('[OSMD-MIDI] Container: synced errorRecognitionActive to osmdEventStream', {
      left: errorRecognitionActive.left,
      right: errorRecognitionActive.right,
    });
  }, [errorRecognitionActive, osmdEventStream]);

  // ---- Event Stream Update Loop ----
  // The osmdEventStream needs update() called periodically to detect missed
  // notes and color them red. This mirrors how MainAppPhaser calls
  // midiStream.update() via scheduler2.setIntervalByTick().
  const startEventStreamUpdateLoop = useCallback(() => {
    if (eventStreamUpdateLoopStartedRef.current || !scheduler2) {
      return;
    }
    eventStreamUpdateLoopStartedRef.current = true;
    logger.debug('[OSMD-MIDI] Container: starting osmdEventStream update loop');
    scheduler2.setIntervalByTick(
      'updateOsmdEventStream',
      () => {
        osmdEventStream.update();
      },
      NoteType._32nd,
      1
    );
  }, [scheduler2, osmdEventStream]);

  // ---- Accuracy Update Loop ----
  // The context's startAccuracyLoop reads from midiStream, which has no
  // events in OSMD mode (events are added to osmdEventStream in the scene).
  // This loop uses osmdEventStream instead and calls props.setAccuracy()
  // to drive the UI accuracy display. The context's loop will get NaN from
  // midiStream.calcRepAccuracy() and skip the setAccuracy call (isNaN guard),
  // so these two loops do not conflict.
  const startAccuracyUpdateLoop = useCallback(() => {
    if (accuracyLoopStartedRef.current || !scheduler2) {
      return;
    }
    accuracyLoopStartedRef.current = true;
    logger.debug('[OSMD-MIDI] Container: starting accuracy update loop using osmdEventStream');
    scheduler2.setIntervalByTick(
      'osmdAccuracyLoop',
      () => {
        const [accuracy] = osmdEventStream.calcRepAccuracy(
          currentTempo,
          performanceTempo ?? currentTempo
        );
        if (!isNaN(accuracy)) {
          props.setAccuracy(accuracy);
        }
      },
      NoteType.QUARTER,
      1
    );
  }, [scheduler2, osmdEventStream, currentTempo, performanceTempo, props.setAccuracy]);

  // ---- Wake Lock ----
  useEffect(() => {
    const acquireWakeLock = async () => {
      if (!('wakeLock' in navigator)) return;
      try {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
        wakeLockRef.current.addEventListener('release', () => {
          wakeLockRef.current = null;
        });
      } catch {
        // Wake lock request failed (e.g. low battery)
      }
    };

    if (!wakeLockRef.current && isPageVisible) {
      acquireWakeLock();
    }
  }, [isPageVisible]);

  // ---- Container sizing via ResizeObserver ----
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleResize = debounce(() => {
      if (document.hidden) return;
      const { clientWidth, clientHeight } = container;
      if (clientWidth > 100 && clientHeight > 100) {
        // Only update the wrapper height (for CSS) — NOT the game dimensions.
        // Updating gameWidth/gameHeight would cause PhaserGame to destroy and
        // recreate the entire Phaser game, wiping all phrases and cursors.
        // The scene's resize() method handles canvas + layout resizing via
        // this.scale.resize() and layoutPhrases().
        setWrapperHeight(clientHeight);
        if (sceneReadyRef.current) {
          NotifyPhaser(ResizeWindow(Math.max(clientWidth, 960), clientHeight));
        }
      }
    }, 100);

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
      handleResize.cancel();
    };
  }, []);

  // ---- Start Phaser scene on PhaserReady ----
  const loadScene = useCallback(() => {
    if (!phaserRef.current?.scene) return;
    logger.debug('[OSMD-MIDI] Container: starting OSMDRepertoireScene', {
      osmdEventStreamId: osmdEventStream.id,
      gameWidth,
      gameHeight,
    });
    // Read from ref so loadScene stays stable (no repertoireSelected in deps).
    // Adding repertoireSelected to deps would cause useCallback to produce a
    // new function reference on every Redux update, which re-registers the
    // PhaserReady listener and risks missing the one-time event.
    const selectedRepertoire = repertoireSelectedRef.current;
    const repertoireTitle = selectedRepertoire?.name ?? '';
    const composerNames = selectedRepertoire?.composers
      ? getNamesConcatted(selectedRepertoire.composers)
      : '';
    logger.debug('[OSMD-MIDI] loadScene title/composer data', {
      hasSelectedRepertoire: !!selectedRepertoire,
      repertoireTitle,
      composerNames,
    });
    phaserRef.current.scene.scene.start('OSMDRepertoireScene', {
      eventStream: osmdEventStream,
      width: gameWidth,
      height: gameHeight,
      performanceTempo: performanceTempo ?? 72,
      title: repertoireTitle,
      composerName: composerNames,
    });
    sceneReadyRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [osmdEventStream, gameWidth, gameHeight]);

  useEffect(() => {
    ListenForPhaserEvent(ListenPhaserReady(loadScene));
    return () => {
      RemoveListener(ExerciseToReactEventType.PhaserReady);
    };
  }, [loadScene]);

  // ---- Send phrases to scene when loaded ----
  useEffect(() => {
    if (allPhrasesLoaded && phrases.length > 0 && !phrasesSentRef.current) {
      phrasesSentRef.current = true;
      logger.debug('[OSMD-MIDI] Container: sending', phrases.length, 'phrases to scene');
      phrases.forEach((phrase, index) => {
        NotifyPhaser(
          AddOSMDPhrase(
            phrase,
            /* showClef */ true,
            /* showBrace */ true,
            /* showTimeSig */ index === 0
          )
        );
      });
    }
  }, [allPhrasesLoaded, phrases]);

  // ---- Forward tempo changes to Phaser ----
  // When the user adjusts the tempo slider, currentTempo (derived from Redux)
  // updates. Forward the change to the scene so the count-in metronome click
  // rate and cursor blink speed update in real time.
  /** Always-current tempo value, readable from callbacks whose closures
   *  don't include currentTempo in their dependency arrays (e.g. the
   *  ListenStartedPlaying handler). */
  const currentTempoRef = useRef(currentTempo);
  currentTempoRef.current = currentTempo;

  const prevTempoRef = useRef(currentTempo);
  useEffect(() => {
    if (currentTempo !== prevTempoRef.current) {
      prevTempoRef.current = currentTempo;
      NotifyPhaser(ChangeTempo(currentTempo));
    }
  }, [currentTempo]);

  // MIDI forwarding to Phaser is now handled by the context's own listener
  // via the forwardMidiToPhaser flag (set above). This ensures MIDI events
  // survive clearListeners() calls in RepertoirePlayContext.init().

  // ---- Play / Pause forwarding to Phaser + event dispatching ----
  useEffect(() => {
    // Skip the very first render to avoid spurious events
    if (isFirstPlayPauseRef.current) {
      isFirstPlayPauseRef.current = false;
      return;
    }

    if (repertoirePlaying) {
      // On resume (after the first MIDI note was already played), recalibrate
      // the shared TimeKeeper BEFORE telling the scene to start updating.
      // Without this, the scene's update() loop reads stale timestamps —
      // TimeKeeper.audioTimeToMeasureTimestamp() uses startTime from the
      // original first play against the ever-advancing Tone.context.currentTime,
      // producing an enormous timestamp that races the cursor to the end.
      // On first play, the deferred pattern is correct: the scene counts in,
      // and startScheduler() is called on the first MIDI note via StartedPlaying.
      if (hasPlayedOnceRef.current) {
        // On resume, bypass startScheduler() which relies on deferred args
        // that may not be available (the context's onStart() stores them,
        // but the effect ordering can cause startScheduler to read null).
        // Instead, directly unpause the scheduler/TimeKeeper here, which is
        // what startScheduler ultimately does anyway.
        // Compute the TimeKeeper's startMeasure from the actual OSMD
        // timestamp rather than the raw measure index. The TimeKeeper's
        // audioTimeToMeasureTimestamp() formula is:
        //   (startMeasure + timeOffset/measureDurationSecs) * measureDurationAt(0)
        // At timeOffset=0 the result is startMeasure * measureDurationAt(0).
        // We want the initial result to equal the OSMD timestamp where the
        // cursor was positioned on pause. So:
        //   startMeasure = pausedAtTimestamp / measureDurationAt(0)
        // This corrects for non-4/4 time signatures where the raw measure
        // index multiplied by the time-sig fraction doesn't match the actual
        // OSMD timestamp.
        const measureDuration = midiStream.timeKeeper.measureDurationAt(0);
        const pausedTimestamp = pausedAtTimestampRef.current;
        const fallbackMeasure = pausedAtMeasureRef.current ?? 0;
        const resumeMeasure =
          pausedTimestamp !== undefined && measureDuration > 0
            ? pausedTimestamp / measureDuration
            : fallbackMeasure;
        logger.info('[UNPAUSE-DBG] container: before unpause', {
          resumeMeasure,
          pausedTimestamp,
          measureDuration,
          fallbackMeasure,
          timeKeeperPausedBefore: midiStream.timeKeeper.isPaused(),
          timeKeeperTimestampBefore: midiStream.timeKeeper.audioTimeToMeasureTimestamp(),
          schedulerLastPauseMeasureBefore: scheduler2?.lastPauseMeasure,
        });
        if (scheduler2) {
          scheduler2.lastPauseMeasure = resumeMeasure;
          scheduler2.unpauseTicks(
            currentTempo,
            1,
            resumeMeasure,
            NoteType._64th
          );
        }
        logger.info('[UNPAUSE-DBG] container: after unpause, before StartExercise', {
          resumeMeasure,
          timeKeeperPausedAfter: midiStream.timeKeeper.isPaused(),
          timeKeeperTimestampAfter: midiStream.timeKeeper.audioTimeToMeasureTimestamp(),
          schedulerLastPauseMeasureAfter: scheduler2?.lastPauseMeasure,
          currentTempo,
        });
        NotifyPhaser(StartExercise(currentTempo));
      } else {
        NotifyPhaser(StartExercise(currentTempo));
      }

      // Dispatch play event to backend
      if (repertoireIdSelect !== undefined && playSessionId) {
        dispatch(
          eventActions.repertoirePlayEventAction(
            repertoireIdSelect,
            playSessionId
          ) as ReturnType<typeof eventActions.repertoirePlayEventAction>
        );
      }

      // Start the 10-second play time reporting interval
      lastMeasuredTimeRef.current = Date.now();
      accumulatedTimeRef.current = 0;

      sendPlayTimeIntervalRef.current = new IntervalTimer(() => {
        if (repertoireIdSelect !== undefined && playSessionId) {
          const now = Date.now();
          accumulatedTimeRef.current +=
            now - lastMeasuredTimeRef.current;
          lastMeasuredTimeRef.current = now;

          const [accuracy, accuracyAtTempo] = osmdEventStream.calcRepAccuracy(
            currentTempo,
            performanceTempo ?? currentTempo
          );
          const totalNotesCount = osmdEventStream.totalNotesLength();
          let completionSum = 0;
          accuracyAtTempo.forEach(
            (value: number) => (completionSum += value)
          );
          const percentComplete =
            totalNotesCount > 0
              ? (completionSum / totalNotesCount) * 100
              : 0;

          dispatch(
            eventActions.repertoirePlayingEventAction(
              repertoireIdSelect,
              playSessionId,
              accumulatedTimeRef.current,
              percentComplete,
              accuracy
            ) as ReturnType<typeof eventActions.repertoirePlayingEventAction>
          );
        }
      }, PLAY_TIME_SEND_INTERVAL_MS);
      sendPlayTimeIntervalRef.current.start();
    } else {
      NotifyPhaser(PauseExercise());

      // Dispatch pause event to backend
      if (repertoireIdSelect !== undefined && playSessionId) {
        dispatch(
          eventActions.repertoirePauseEventAction(
            repertoireIdSelect,
            playSessionId
          ) as ReturnType<typeof eventActions.repertoirePauseEventAction>
        );
      }

      // Stop the play time interval
      if (sendPlayTimeIntervalRef.current) {
        sendPlayTimeIntervalRef.current.pause();
        sendPlayTimeIntervalRef.current.clear();
        sendPlayTimeIntervalRef.current = undefined;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repertoirePlaying]);

  // ---- Listen for StartedPlaying from Phaser ----
  // When the user plays the first note during count-in, the Phaser scene
  // emits StartedPlaying with a processEventsQueue callback. We must
  // invoke that callback to flush the queued first-note MIDI event
  // through the event stream so it gets matched and colored.
  // We also call startScheduler() here to perform the deferred
  // scheduler2.unpauseTicks(), mirroring how MainAppPhaser calls onStart
  // only when it receives StartedPlaying from the scene.
  useEffect(() => {
    ListenForPhaserEvent(
      ListenStartedPlaying(async (args?: StartPlayingArgs) => {
        logger.debug('[OSMD-MIDI] Container: StartedPlaying received', {
          hasProcessEventsQueue: !!args?.processEventsQueue,
          measureOffset: args?.measureOffset,
          timeKeeperPausedBefore: midiStream.timeKeeper.isPaused(),
          hasPlayedOnce: hasPlayedOnceRef.current,
        });

        // On first play only: start the shared TimeKeeper now that the
        // first MIDI note has been played during count-in.
        // On resume, startScheduler() was already called in the play/pause
        // effect (before StartExercise was sent to the scene), so the
        // TimeKeeper is already recalibrated — calling it again is a no-op
        // (deferredUnpauseArgs was already consumed).
        if (!hasPlayedOnceRef.current) {
          // Must await because startScheduler is async (calls scheduler2.unpauseTicks).
          // The TimeKeeper must be unpaused BEFORE processEventsQueue runs,
          // otherwise handleNoteOnEvent will early-return on isPaused().
          // Pass measureOffset so the scheduler's lastPauseMeasure is set
          // correctly, aligning TimeKeeper timestamps with event buckets.
          await startScheduler(args?.measureOffset);

          // Correct the TimeKeeper BPM if the user adjusted the tempo
          // slider during count-in. The context's deferred scheduler args
          // captured the tempo at the moment play was pressed — if the user
          // changed BPM while the cursor was blinking, the TimeKeeper was
          // just initialized with that stale value. Calling setBpm()
          // immediately after unpause recalculates measureDurationSecs,
          // secondsPerTick, and startTime so playback proceeds at the
          // user's latest chosen tempo.
          const latestTempo = currentTempoRef.current;
          if (midiStream.timeKeeper.getBpm() !== latestTempo) {
            logger.debug('[OSMD-MIDI] Container: correcting TimeKeeper BPM after startScheduler', {
              staleBpm: midiStream.timeKeeper.getBpm(),
              correctBpm: latestTempo,
            });
            midiStream.timeKeeper.setBpm(latestTempo);
          }

          hasPlayedOnceRef.current = true;
        }

        logger.debug('[OSMD-MIDI] Container: after startScheduler()', {
          timeKeeperPausedAfter: midiStream.timeKeeper.isPaused(),
        });
        // Start the event stream update loop now that the scheduler is running.
        // This calls osmdEventStream.update() periodically to detect missed
        // notes and color them red (NEVER_PLAYED state).
        startEventStreamUpdateLoop();
        // Start the accuracy update loop so the UI accuracy display reflects
        // notes matched in osmdEventStream (not the context's empty midiStream).
        startAccuracyUpdateLoop();
        if (args?.processEventsQueue) {
          const [processQueueFn, processQueueScope] = args.processEventsQueue;
          logger.debug('[OSMD-MIDI] Container: calling processEventsQueue NOW');
          processQueueFn.call(processQueueScope);
          logger.debug('[OSMD-MIDI] Container: processEventsQueue returned');
        }
      })
    );
    return () => {
      RemoveListener(ExerciseToReactEventType.StartedPlaying);
    };
  }, [startScheduler, startEventStreamUpdateLoop, startAccuracyUpdateLoop]);

  // ---- Listen for ExercisePaused from Phaser ----
  // When the scene pauses and repositions the cursor to the last completed
  // measure, it emits ExercisePaused(lastPlayedMeasure). We store this so
  // that on resume, startScheduler() can pass it as measureOffset, ensuring
  // the TimeKeeper starts from exactly where the cursor was placed.
  useEffect(() => {
    ListenForPhaserEvent(
      ListenExercisePaused(
        ({ lastPlayedMeasure, pausedAtTimestamp }: { lastPlayedMeasure?: number; pausedAtTimestamp?: number }) => {
          logger.debug(
            '[OSMD-MIDI] Container: ExercisePaused received',
            { lastPlayedMeasure, pausedAtTimestamp }
          );
          pausedAtMeasureRef.current = lastPlayedMeasure;
          pausedAtTimestampRef.current = pausedAtTimestamp;
        }
      )
    );
    return () => {
      RemoveListener(ExerciseToReactEventType.ExercisePaused);
    };
  }, []);

  // ---- Listen for Phaser completion events ----
  useEffect(() => {
    ListenForPhaserEvent(
      ListenRepertoireCompleted(() => {
        logger.info(
          'RepertoirePlayContainerOSMD: RepertoireCompleted received from Phaser'
        );
        if (repertoireIdSelect !== undefined && playSessionId) {
          const [accuracy, accuracyAtTempo] = osmdEventStream.calcRepAccuracy(
            currentTempo,
            performanceTempo ?? currentTempo
          );
          const totalNotesCount = osmdEventStream.totalNotesLength();
          let completionSum = 0;
          accuracyAtTempo.forEach(
            (value: number) => (completionSum += value)
          );
          const completion =
            totalNotesCount > 0
              ? (completionSum / totalNotesCount) * 100
              : 0;

          dispatch(
            eventActions.repertoireCompletedEventAction(
              repertoireIdSelect,
              playSessionId,
              completion,
              accuracy
            ) as ReturnType<typeof eventActions.repertoireCompletedEventAction>
          );
        }
      })
    );

    ListenForPhaserEvent(
      ListenRepertoirePhraseCompleted(
        ({ phraseIndex }: { phraseIndex: number }) => {
          const phrase = phrases[phraseIndex];
          if (phrase && playSessionId && repertoireIdSelect !== undefined) {
            // Delay dispatch by 2 seconds to capture the final note offset
            setTimeout(() => {
              if (phrase.getComplete()) return;
              phrase.setComplete(true);

              const phraseData = phrase.generateRepPhraseCompletedEventData();
              dispatch(
                eventActions.repPhraseCompletedEventAction({
                  midiData: [],
                  errorData: phraseData.errorData,
                  accuracy: phraseData.accuracy,
                  lHAccuracy: phraseData.lhAccuracy,
                  rHAccuracy: phraseData.rhAccuracy,
                  totalNotes: phraseData.totalNotes,
                  lhTotalNotes: phraseData.lhTotalNotes,
                  rhTotalNotes: phraseData.rhTotalNotes,
                  errorScore: phraseData.errorScore,
                  lhErrorScore: phraseData.lhErrorScore,
                  rhErrorScore: phraseData.rhErrorScore,
                  phraseS3Id: phrase.uuid,
                  repertoireId: repertoireIdSelect,
                  bpm: phraseData.bpm,
                  playSessionId,
                }) as ReturnType<typeof eventActions.repPhraseCompletedEventAction>
              );
            }, 2000);
          }
        }
      )
    );

    return () => {
      RemoveListener(ExerciseToReactEventType.RepertoireCompleted);
      RemoveListener(ExerciseToReactEventType.RepertoirePhraseCompleted);
    };
  }, [
    phrases,
    playSessionId,
    repertoireIdSelect,
    currentTempo,
    performanceTempo,
    midiStream,
    dispatch,
  ]);

  // ---- Forward soft-refresh to Phaser ----
  // RepertoirePlayContext.softRefreshRepertoire() increments updateKey.
  // When updateKey changes, we know a soft refresh occurred and notify the scene.
  useEffect(() => {
    if (updateKey !== prevUpdateKeyRef.current) {
      prevUpdateKeyRef.current = updateKey;
      logger.info(
        'RepertoirePlayContainerOSMD: soft refresh detected, notifying Phaser'
      );
      NotifyPhaser(SoftRefreshRepertoireEvent());

      // Reset phrase-sent flag so phrases can be re-sent if needed after refresh
      // (In practice, phrases are already loaded; the scene's softReset handles visuals)
      phrasesSentRef.current = true;

      // Reset the update loop flags so they can be restarted on next play
      eventStreamUpdateLoopStartedRef.current = false;
      accuracyLoopStartedRef.current = false;

      // Reset first-play tracking so the next play cycle uses the deferred
      // scheduler pattern (count-in → first MIDI note → startScheduler)
      hasPlayedOnceRef.current = false;

      // Clear the paused measure/timestamp — fresh play cycle after soft refresh
      pausedAtMeasureRef.current = undefined;
      pausedAtTimestampRef.current = undefined;
    }
  }, [updateKey]);

  // ---- Cleanup on unmount ----
  useEffect(() => {
    return () => {
      if (sendPlayTimeIntervalRef.current) {
        sendPlayTimeIntervalRef.current.pause();
        sendPlayTimeIntervalRef.current.clear();
        sendPlayTimeIntervalRef.current = undefined;
      }
      RemoveListener(ExerciseToReactEventType.PhaserReady);
      RemoveListener(ExerciseToReactEventType.StartedPlaying);
      RemoveListener(ExerciseToReactEventType.RepertoireCompleted);
      RemoveListener(ExerciseToReactEventType.RepertoirePhraseCompleted);
    };
  }, []);

  // ---- Render ----
  return (
    <div
      ref={containerRef}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        width: '100vw',
        flex: 1,          // fill parent height (main container has flex: 1 in RepertoirePlay)
        minHeight: 0,     // allow flex shrink below content size
      }}
    >
      {musicLoaded && allPhrasesLoaded ? (
        <div
          className="osmd-repertoire-game-wrapper"
          style={{
            width: '100%',
            height: `${wrapperHeight}px`,
            backgroundColor: 'rgb(235, 235, 235)',
          }}
        >
          {/*
            PhaserGame sets pointer-events: none on #game-container for the
            lesson section (where the game area shouldn't capture clicks).
            Repertoire needs pointer events for scroll input, so we re-enable
            them via an inline <style> scoped to this wrapper.
          */}
          <style>{`.osmd-repertoire-game-wrapper #game-container { pointer-events: auto !important; }`}</style>
          <PhaserGame
            width={gameWidth}
            height={gameHeight}
            ref={phaserRef}
            scaling={Phaser.Scale.NONE}
            crisp={true}
          />
        </div>
      ) : (
        <CustomCircularProgress />
      )}
    </div>
  );
};

export default RepertoirePlayContainerOSMD;
